import React from 'react';
import { XEnv } from '../../data.jsx';
import { XIcons, XPrim } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { SearchableSelect } from '../../ui/searchable-select.jsx';
import WsRealtimeOrb from './ws-realtime-orb.jsx';
import { resolveOpenAiLiveAuthorizationDisplay } from './openai-live-pricing.js';
import {
  AUDIO_RATE,
  AI_GATEWAY_STRATEGIES,
  CLIENT_DELEGATION_DEBOUNCE_MS,
  DEFAULT_DELEGATION_MODEL,
  LIVE_BUILTIN_VOICES,
  MAX_TRANSCRIPT_CHARS,
  appendTranscriptFragment,
  buildClientDelegationRequest,
  buildCommentaryAppend,
  buildLiveSessionStart,
  buildTypedLiveEvents,
  createEventId,
  estimateOfficialDelegationUsage,
  hasWebSocketSendCapacity,
  normalizeResponseModels,
  responseOutputText,
  transcriptFragmentAtOrBeforeOffset,
} from './openai-live-client.js';

const APPEND_ACK_TIMEOUT_MS = 10_000;
const CLIENT_DELEGATION_REQUEST_TIMEOUT_MS = 60_000;

const PHASE_LABEL = {
  idle: '未连接',
  connecting: '连接中…',
  ready: '准备就绪',
  microphone: '正在开启麦克风…',
  listening: '聆听中…',
  delegating: '正在委派…',
  thinking: '思考中…',
  speaking: '说话中…',
};

function bytesToBase64(bytes) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1)
    bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function floatToPcm16(float32, inputRate) {
  const ratio = inputRate / AUDIO_RATE;
  const output = new Int16Array(Math.floor(float32.length / ratio));
  for (let index = 0; index < output.length; index += 1) {
    const sample = Math.max(
      -1,
      Math.min(1, float32[Math.floor(index * ratio)]),
    );
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return new Uint8Array(output.buffer);
}

function isSecureAudioContext() {
  return (
    typeof window !== 'undefined' &&
    (window.isSecureContext ||
      /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname))
  );
}

function gatewayError(result) {
  if (result?.error) return result.error;
  const body = result?.body;
  const detail = body?.error?.message || body?.message || body?.error;
  return typeof detail === 'string'
    ? detail
    : `HTTP ${result?.status || 'error'}`;
}

export default function WsPlaygroundLive({ api, endpoint }) {
  const wsConfig = endpoint?._raw?.wsConfig || {};
  const wsBilling = endpoint?._raw?.wsBilling || {};
  const clientPath = wsConfig.clientPath || '/v1/live/sessions';
  const configuredMode = wsConfig.openAiLiveDelegation?.type;
  const selectableMode = configuredMode === 'selectable';
  const fixedMode = configuredMode === 'responses' ? 'responses' : 'client';
  const officialResponsesModel =
    wsConfig.openAiLiveDelegation?.responses?.model || 'gpt-5.6-sol';

  const [mode, setMode] = React.useState(fixedMode);
  const [voice, setVoice] = React.useState('marin');
  const [instructions, setInstructions] = React.useState('');
  const [status, setStatus] = React.useState('idle');
  const [phase, setPhase] = React.useState('idle');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [input, setInput] = React.useState('');
  const [turns, setTurns] = React.useState([]);
  const [models, setModels] = React.useState([]);
  const [modelId, setModelId] = React.useState('');
  const [strategy, setStrategy] = React.useState('default');
  const [modelsLoading, setModelsLoading] = React.useState(true);
  const [modelsError, setModelsError] = React.useState('');
  const [delegations, setDelegations] = React.useState([]);
  const [liveSeconds, setLiveSeconds] = React.useState(0);
  const [delegationUsage, setDelegationUsage] = React.useState({
    input: 0,
    cacheWrite: 0,
    cached: 0,
    output: 0,
    estimatedCost: 0,
  });
  const modelOptions = React.useMemo(
    () =>
      models.map((model) => ({
        ...model,
        value: model.id,
        label: model.id,
      })),
    [models],
  );

  const wsRef = React.useRef(null);
  const connectionAttemptRef = React.useRef(0);
  const statusRef = React.useRef('idle');
  const modeRef = React.useRef(mode);
  const transcriptRef = React.useRef([]);
  const sessionStartedAtRef = React.useRef(0);
  const handledDelegationsRef = React.useRef(new Set());
  const completedResponsesRef = React.useRef(new Set());
  const pendingAppendRef = React.useRef(new Map());
  const requestControllersRef = React.useRef(new Map());
  const turnIdRef = React.useRef(0);
  const micContextRef = React.useRef(null);
  const micStreamRef = React.useRef(null);
  const micNodeRef = React.useRef(null);
  const micRequestRef = React.useRef(null);
  const startMicrophoneRef = React.useRef(null);
  const playbackContextRef = React.useRef(null);
  const playbackAtRef = React.useRef(0);
  const playbackSourcesRef = React.useRef([]);
  const micAnalyserRef = React.useRef(null);
  const outputAnalyserRef = React.useRef(null);

  const selectedModel = models.find((model) => model.id === modelId) || null;
  const connected =
    status === 'ready' || status === 'recording' || status === 'connecting';
  const ready = status === 'ready' || status === 'recording';
  const livePerMinuteUsd = Math.max(
    0,
    Number(wsBilling.perMinuteUsd) || 0,
  );
  const liveEstimatedCost = (liveSeconds / 60) * livePerMinuteUsd;
  const authorization = resolveOpenAiLiveAuthorizationDisplay(endpoint);

  const setStatusBoth = React.useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  React.useEffect(() => {
    if (!selectableMode) setMode(fixedMode);
  }, [fixedMode, selectableMode]);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setModelsLoading(true);
    setModelsError('');
    XApi.aiGateway
      .listResponseModels({ signal: controller.signal })
      .then((responseModels) => {
        if (cancelled) return;
        const resolved = normalizeResponseModels(responseModels);
        setModels(resolved);
        setModelId((current) => {
          if (resolved.some((model) => model.id === current)) return current;
          if (resolved.some((model) => model.id === DEFAULT_DELEGATION_MODEL)) {
            return DEFAULT_DELEGATION_MODEL;
          }
          return resolved[0]?.id || '';
        });
        if (!resolved.length)
          setModelsError(
            'xAPI AI Gateway 当前没有可通过 Responses 调用的文本模型。',
          );
      })
      .catch((error) => {
        if (!cancelled && error?.name !== 'AbortError')
          setModelsError(error?.message || '无法加载 AI Gateway 模型目录。');
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const stopPlayback = React.useCallback(() => {
    for (const source of playbackSourcesRef.current) {
      try {
        source.stop();
      } catch {}
    }
    playbackSourcesRef.current = [];
    if (playbackContextRef.current)
      playbackAtRef.current = playbackContextRef.current.currentTime;
  }, []);

  const stopMicrophone = React.useCallback(() => {
    micRequestRef.current = null;
    if (micNodeRef.current) {
      try {
        micNodeRef.current.disconnect();
      } catch {}
      micNodeRef.current = null;
    }
    if (micContextRef.current) {
      try {
        micContextRef.current.close();
      } catch {}
      micContextRef.current = null;
    }
    if (micStreamRef.current) {
      try {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch {}
      micStreamRef.current = null;
    }
    micAnalyserRef.current = null;
  }, []);

  const teardown = React.useCallback(
    (closeSocket = true) => {
      stopMicrophone();
      stopPlayback();
      if (playbackContextRef.current) {
        try {
          playbackContextRef.current.close();
        } catch {}
        playbackContextRef.current = null;
      }
      outputAnalyserRef.current = null;
      playbackAtRef.current = 0;
      for (const request of requestControllersRef.current.values()) {
        if (request.timer) clearTimeout(request.timer);
        if (request.timeout) clearTimeout(request.timeout);
        request.controller?.abort();
      }
      requestControllersRef.current.clear();
      for (const pending of pendingAppendRef.current.values()) {
        if (pending.timer) clearTimeout(pending.timer);
      }
      pendingAppendRef.current.clear();
      if (closeSocket && wsRef.current) {
        try {
          // xAPI gateway owns the upstream graceful-close handshake and final usage wait.
          // Closing only the browser transport avoids sending session.close twice.
          wsRef.current.close(1000, 'client stop');
        } catch {}
      }
    },
    [stopMicrophone, stopPlayback],
  );

  React.useEffect(
    () => () => {
      connectionAttemptRef.current += 1;
      teardown(true);
    },
    [teardown],
  );

  const send = React.useCallback((event) => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    const payload = JSON.stringify(event);
    const payloadBytes = new TextEncoder().encode(payload).byteLength;
    if (!hasWebSocketSendCapacity(socket, payloadBytes)) {
      setErrorMessage(
        '网络发送缓冲已满，已停止继续发送音频；请等待网络恢复后重连。',
      );
      return false;
    }
    socket.send(payload);
    return true;
  }, []);

  const appendTurn = React.useCallback(
    (role, delta, streaming = true, forceNew = false) => {
      if (!delta) return;
      setTurns((current) => {
        const last = current.at(-1);
        if (!forceNew && last?.role === role && last.streaming) {
          return current.map((turn, index) =>
            index === current.length - 1
              ? { ...turn, text: turn.text + delta, streaming }
              : turn,
          );
        }
        return [
          ...current,
          {
            id: `live-turn-${turnIdRef.current++}`,
            role,
            text: delta,
            streaming,
          },
        ];
      });
    },
    [],
  );

  const ensurePlaybackContext = React.useCallback(() => {
    if (!playbackContextRef.current) {
      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;
      const context = new AudioContextClass();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      analyser.connect(context.destination);
      playbackContextRef.current = context;
      outputAnalyserRef.current = analyser;
      playbackAtRef.current = context.currentTime;
    }
    const context = playbackContextRef.current;
    if (context.state === 'suspended') void context.resume();
    return context;
  }, []);

  const playAudio = React.useCallback(
    (base64) => {
      try {
        const context = ensurePlaybackContext();
        const bytes = base64ToBytes(base64);
        const view = new DataView(
          bytes.buffer,
          bytes.byteOffset,
          bytes.byteLength,
        );
        const sampleCount = Math.floor(bytes.byteLength / 2);
        const buffer = context.createBuffer(1, sampleCount, AUDIO_RATE);
        const channel = buffer.getChannelData(0);
        for (let index = 0; index < sampleCount; index += 1) {
          channel[index] = view.getInt16(index * 2, true) / 0x8000;
        }
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(outputAnalyserRef.current || context.destination);
        source.onended = () => {
          playbackSourcesRef.current = playbackSourcesRef.current.filter(
            (item) => item !== source,
          );
        };
        playbackSourcesRef.current.push(source);
        const startAt = Math.max(playbackAtRef.current, context.currentTime);
        source.start(startAt);
        playbackAtRef.current = startAt + buffer.duration;
      } catch {
        setErrorMessage('收到无法播放的 GPT Live 音频帧。');
      }
    },
    [ensurePlaybackContext],
  );

  const updateDelegation = React.useCallback((id, patch) => {
    setDelegations((current) => {
      const exists = current.some((item) => item.id === id);
      if (!exists) return [...current, { id, ...patch }];
      return current.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      );
    });
  }, []);

  const invokeClientBackend = React.useCallback(
    async ({ jobId, generation }) => {
      const job = requestControllersRef.current.get(jobId);
      if (!job || job.generation !== generation) return;
      const { delegationId, offsetMs, source } = job;
      updateDelegation(jobId, { mode: source, status: 'running' });
      setPhase('delegating');

      const delegationModel = models.find((item) => item.id === modelId);
      const apiKey = XEnv.get('XAPI_KEY');
      if (!delegationModel || !apiKey) {
        const message = !delegationModel
          ? 'AI Gateway Responses 模型尚未就绪。'
          : '缺少 XAPI_KEY，请先在 Console 创建或选择一个 xAPI Key。';
        updateDelegation(jobId, { status: 'failed', error: message });
        requestControllersRef.current.delete(jobId);
        setErrorMessage(message);
        setPhase('ready');
        return;
      }

      const controller = new AbortController();
      job.controller = controller;
      job.status = 'running';
      job.timedOut = false;
      const requestTimeout = setTimeout(() => {
        job.timedOut = true;
        controller.abort();
      }, CLIENT_DELEGATION_REQUEST_TIMEOUT_MS);
      job.timeout = requestTimeout;
      const request = buildClientDelegationRequest(
        transcriptRef.current,
        offsetMs,
        delegationModel.id,
      );

      try {
        const result = await XApi.aiGateway.createResponse({
          strategy,
          apiKey,
          body: request,
          signal: controller.signal,
        });
        const current = requestControllersRef.current.get(jobId);
        if (controller.signal.aborted) {
          if (job.timedOut) throw new Error('client delegation timed out');
          return;
        }
        if (
          !current ||
          current.generation !== generation ||
          current.controller !== controller
        )
          return;
        clearTimeout(requestTimeout);
        current.timeout = null;
        if (!result.ok) throw new Error(gatewayError(result));
        const content = responseOutputText(result.body);
        if (!content)
          throw new Error(`${delegationModel.id} 没有返回可回灌的文本。`);
        const append = buildCommentaryAppend(delegationId, content);
        if (!send(append))
          throw new Error('Live 会话已断开，无法回灌委派结果。');
        const timer = setTimeout(() => {
          const pending = pendingAppendRef.current.get(append.event_id);
          if (!pending || pending.generation !== generation) return;
          pendingAppendRef.current.delete(append.event_id);
          const latest = requestControllersRef.current.get(jobId);
          if (latest?.generation !== generation || latest.status !== 'sent')
            return;
          requestControllersRef.current.delete(jobId);
          const message = 'GPT Live 未确认接收委派结果，请重试本轮请求。';
          updateDelegation(jobId, { status: 'failed', error: message });
          setErrorMessage(message);
          setPhase('ready');
        }, APPEND_ACK_TIMEOUT_MS);
        pendingAppendRef.current.set(append.event_id, {
          jobId,
          generation,
          timer,
        });
        const usage = result.body?.usage || {};
        setDelegationUsage((current) => ({
          input: current.input + (Number(usage.input_tokens) || 0),
          cacheWrite:
            current.cacheWrite +
            (Number(usage.input_tokens_details?.cache_write_tokens) || 0),
          cached:
            current.cached +
            (Number(usage.input_tokens_details?.cached_tokens) || 0),
          output: current.output + (Number(usage.output_tokens) || 0),
          estimatedCost: current.estimatedCost,
        }));
        updateDelegation(jobId, {
          status: 'sent',
          content,
          model: delegationModel.id,
          strategy,
        });
        current.status = 'sent';
        current.controller = null;
        setPhase('thinking');
      } catch (error) {
        if (
          !job.timedOut &&
          (controller.signal.aborted || error?.name === 'AbortError')
        )
          return;
        const message = job.timedOut
          ? 'client delegation 调用超时，请重试。'
          : error?.message || 'client delegation 调用失败';
        updateDelegation(jobId, { status: 'failed', error: message });
        const current = requestControllersRef.current.get(jobId);
        if (current?.generation === generation) {
          requestControllersRef.current.delete(jobId);
        }
        setErrorMessage(message);
        try {
          send(
            buildCommentaryAppend(
              delegationId,
              '委派请求失败，请稍后重试。',
              createEventId('delegation-failed'),
            ),
          );
        } catch {}
        setPhase('ready');
      } finally {
        clearTimeout(requestTimeout);
        const current = requestControllersRef.current.get(jobId);
        if (
          current?.generation === generation &&
          current.controller === controller
        ) {
          current.controller = null;
          current.timeout = null;
        }
      }
    },
    [modelId, models, send, strategy, updateDelegation],
  );

  const scheduleClientBackend = React.useCallback(
    ({ jobId, delegationId, offsetMs, source, delayMs }) => {
      const previous = requestControllersRef.current.get(jobId);
      if (previous?.timer) clearTimeout(previous.timer);
      if (previous?.timeout) clearTimeout(previous.timeout);
      previous?.controller?.abort();
      const generation = (previous?.generation || 0) + 1;
      const job = {
        jobId,
        delegationId,
        offsetMs,
        source,
        generation,
        status: 'scheduled',
        controller: null,
        timer: null,
        timeout: null,
        timedOut: false,
      };
      job.timer = setTimeout(() => {
        const current = requestControllersRef.current.get(jobId);
        if (!current || current.generation !== generation) return;
        current.timer = null;
        void invokeClientBackend({ jobId, generation });
      }, delayMs);
      requestControllersRef.current.set(jobId, job);
      updateDelegation(jobId, { mode: source, status: 'queued' });
    },
    [invokeClientBackend, updateDelegation],
  );

  const restartClientDelegationsForTranscript = React.useCallback(
    (event) => {
      for (const [jobId, request] of requestControllersRef.current.entries()) {
        if (request.source !== 'client') continue;
        // A transcript delta is a fragment, not a turn boundary. Refresh only the current local
        // work that has not produced a result yet. Sent/accepted jobs belong to an ordered Live
        // context update and must not be replayed merely because the user starts another turn.
        if (request.status !== 'scheduled' && request.status !== 'running')
          continue;
        // delegation.created.offset_ms is an immutable timeline snapshot. Only a transcript
        // fragment delivered late but ending inside that snapshot may refresh the request; a
        // later user turn must wait for its own delegation.created event.
        if (!transcriptFragmentAtOrBeforeOffset(event, request.offsetMs))
          continue;
        scheduleClientBackend({
          jobId,
          delegationId: request.delegationId,
          offsetMs: request.offsetMs,
          source: request.source,
          delayMs: CLIENT_DELEGATION_DEBOUNCE_MS,
        });
      }
    },
    [scheduleClientBackend],
  );

  const runClientDelegation = React.useCallback(
    (event) => {
      const delegationId = event?.delegation?.id;
      if (!delegationId || handledDelegationsRef.current.has(delegationId))
        return;
      handledDelegationsRef.current.add(delegationId);
      // Delegation IDs are independent upstream work items. A newer request must not silently
      // cancel an older one, because Live still expects a result correlated to every opaque ID.
      scheduleClientBackend({
        jobId: delegationId,
        delegationId,
        offsetMs: event.offset_ms,
        source: 'client',
        delayMs: CLIENT_DELEGATION_DEBOUNCE_MS,
      });
    },
    [scheduleClientBackend],
  );

  const runTypedClientInput = React.useCallback(() => {
    // Typed input is another independent turn. It must not abandon a Live-created delegation
    // whose result is still required by the session.
    const jobId = createEventId('typed');
    scheduleClientBackend({
      jobId,
      delegationId: null,
      offsetMs: Number.POSITIVE_INFINITY,
      source: 'client-typed',
      delayMs: 0,
    });
  }, [scheduleClientBackend]);

  const handleEvent = React.useCallback(
    (event) => {
      const type = event?.type || '';
      if (type === 'error') {
        const message =
          event.error?.message || JSON.stringify(event.error || event);
        const clientEventId =
          event.error?.client_event_id || event.client_event_id;
        const pending = pendingAppendRef.current.get(clientEventId);
        if (pending) {
          pendingAppendRef.current.delete(clientEventId);
          if (pending.timer) clearTimeout(pending.timer);
          const current = requestControllersRef.current.get(pending.jobId);
          if (current?.generation === pending.generation) {
            requestControllersRef.current.delete(pending.jobId);
            updateDelegation(pending.jobId, {
              status: 'failed',
              error: message,
            });
            setPhase('ready');
          }
        }
        setErrorMessage(message);
        return;
      }
      if (type === 'session.started') {
        const resolvedMode = event.session?.delegation?.type;
        if (resolvedMode !== modeRef.current) {
          setErrorMessage(
            `端点将 delegation 模式解析为 ${resolvedMode || 'unknown'}，与前端选择的 ${modeRef.current} 不一致，连接已关闭。`,
          );
          setStatusBoth('error');
          setPhase('idle');
          try {
            wsRef.current?.close(1008, 'delegation mode mismatch');
          } catch {}
          return;
        }
        sessionStartedAtRef.current = performance.now();
        setStatusBoth('ready');
        void startMicrophoneRef.current?.();
        return;
      }
      if (type === 'session.input_transcript.delta') {
        transcriptRef.current = appendTranscriptFragment(
          transcriptRef.current,
          'user',
          event,
        );
        appendTurn('user', event.delta || '');
        if (modeRef.current === 'client') {
          restartClientDelegationsForTranscript(event);
        }
        return;
      }
      if (type === 'session.output_transcript.delta') {
        transcriptRef.current = appendTranscriptFragment(
          transcriptRef.current,
          'assistant',
          event,
        );
        appendTurn('assistant', event.delta || '');
        if (modeRef.current === 'client') {
          restartClientDelegationsForTranscript(event);
        }
        return;
      }
      if (
        type === 'session.input_audio.speech_started' ||
        type === 'session.input_audio.started'
      ) {
        stopPlayback();
        setPhase('listening');
        return;
      }
      if (type === 'session.output_audio.delta') {
        if (event.delta) playAudio(event.delta);
        setPhase('speaking');
        return;
      }
      if (type === 'session.delegation.created') {
        const id = event.delegation?.id;
        const target = event.delegation?.target;
        if (id)
          updateDelegation(id, {
            mode: target || modeRef.current,
            status: target === 'client' ? 'queued' : 'running',
            ...(target === 'responses'
              ? { model: officialResponsesModel }
              : {}),
          });
        if (target === 'client' && modeRef.current === 'client') {
          void runClientDelegation(event);
        } else if (target === 'client') {
          const message =
            '端点返回了 client delegation，但当前会话选择的是 Official 模式。';
          if (id) updateDelegation(id, { status: 'failed', error: message });
          setErrorMessage(message);
          setPhase('ready');
        } else {
          setPhase('delegating');
        }
        return;
      }
      if (type === 'session.commentary.appended') {
        const pending = pendingAppendRef.current.get(event.client_event_id);
        if (pending) {
          pendingAppendRef.current.delete(event.client_event_id);
          if (pending.timer) clearTimeout(pending.timer);
          const current = requestControllersRef.current.get(pending.jobId);
          if (current?.generation === pending.generation) {
            current.status = 'accepted';
            updateDelegation(pending.jobId, { status: 'accepted' });
            requestControllersRef.current.delete(pending.jobId);
          }
        }
        return;
      }
      if (type === 'response.event') {
        const nested = event.event || {};
        const delegationId = event.delegation_id;
        if (
          nested.type === 'response.output_text.delta' ||
          nested.type === 'response.refusal.delta'
        ) {
          if (delegationId) {
            setDelegations((current) =>
              current.map((item) =>
                item.id === delegationId
                  ? {
                      ...item,
                      status: 'running',
                      content: `${item.content || ''}${nested.delta || ''}`,
                    }
                  : item,
              ),
            );
          }
          return;
        }
        if (
          [
            'response.completed',
            'response.incomplete',
            'response.failed',
          ].includes(nested.type)
        ) {
          const responseId = nested.response?.id;
          const terminalIdentities = [
            delegationId ? `delegation:${delegationId}` : null,
            responseId ? `response:${responseId}` : null,
            event.event_id ? `event:${event.event_id}` : null,
          ].filter(Boolean);
          if (
            terminalIdentities.some((identity) =>
              completedResponsesRef.current.has(identity),
            )
          ) {
            return;
          }
          for (const identity of terminalIdentities) {
            completedResponsesRef.current.add(identity);
          }
          const usage = nested.response?.usage || {};
          const pricedUsage = estimateOfficialDelegationUsage(
            usage,
            wsBilling,
          );
          setDelegationUsage((current) => ({
            input: current.input + pricedUsage.input,
            cacheWrite: current.cacheWrite + pricedUsage.cacheWrite,
            cached: current.cached + pricedUsage.cached,
            output: current.output + pricedUsage.output,
            estimatedCost:
              current.estimatedCost + pricedUsage.estimatedCost,
          }));
          if (delegationId) {
            updateDelegation(delegationId, {
              status:
                nested.type === 'response.completed' ? 'completed' : 'failed',
              error: nested.response?.error?.message || undefined,
            });
          }
          setPhase('thinking');
        }
        return;
      }
      if (type === 'session.usage.updated' || type === 'session.closed') {
        if (Number.isFinite(event.usage?.seconds))
          setLiveSeconds(event.usage.seconds);
        if (type === 'session.closed') {
          teardown(false);
          setStatusBoth('closed');
          setPhase('idle');
        }
      }
    },
    [
      appendTurn,
      restartClientDelegationsForTranscript,
      playAudio,
      runClientDelegation,
      setStatusBoth,
      stopPlayback,
      teardown,
      updateDelegation,
      officialResponsesModel,
      wsBilling,
    ],
  );

  const connect = async () => {
    const connectionAttempt = connectionAttemptRef.current + 1;
    connectionAttemptRef.current = connectionAttempt;
    // A terminal Live event can make the reconnect action visible shortly before the old
    // transport emits close. Tear it down now; identity guards below keep its late callbacks from
    // mutating the replacement session.
    if (wsRef.current) {
      teardown(true);
      wsRef.current = null;
    }
    setErrorMessage('');
    if (mode === 'client' && (!selectedModel || !XEnv.get('XAPI_KEY'))) {
      setErrorMessage(
        !selectedModel
          ? modelsError || 'AI Gateway Responses 模型尚未就绪。'
          : 'client 模式需要 XAPI_KEY，请先在 Console 创建或选择一个 xAPI Key。',
      );
      return;
    }
    // Create/resume the output context while the click still carries browser user activation.
    // Otherwise Safari/Chrome may receive valid audio frames but keep playback suspended.
    try {
      ensurePlaybackContext();
    } catch {
      setErrorMessage('浏览器无法初始化音频播放。');
      return;
    }
    setTurns([]);
    setDelegations([]);
    setLiveSeconds(0);
    setDelegationUsage({
      input: 0,
      cacheWrite: 0,
      cached: 0,
      output: 0,
      estimatedCost: 0,
    });
    transcriptRef.current = [];
    handledDelegationsRef.current = new Set();
    completedResponsesRef.current = new Set();
    pendingAppendRef.current = new Map();
    modeRef.current = mode;
    setStatusBoth('connecting');
    setPhase('connecting');

    let token;
    try {
      const minted = await XApi.keyService.getWsToken(
        endpoint?._raw?.id || endpoint?.id,
      );
      if (connectionAttemptRef.current !== connectionAttempt) return;
      token = minted?.token;
      if (!token) throw new Error('ws-token mint returned no token');
    } catch (error) {
      if (connectionAttemptRef.current !== connectionAttempt) return;
      setStatusBoth('error');
      setPhase('idle');
      setErrorMessage(`ws-token mint 失败：${error?.message || error}`);
      return;
    }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, {
      endpoint: endpoint?._raw?.id || endpoint?.id,
    });
    let socket;
    try {
      socket = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token));
    } catch (error) {
      if (connectionAttemptRef.current !== connectionAttempt) return;
      setStatusBoth('error');
      setPhase('idle');
      setErrorMessage(`WebSocket 构造失败：${error?.message || error}`);
      return;
    }
    wsRef.current = socket;
    socket.onopen = () => {
      if (wsRef.current !== socket) return;
      socket.send(
        JSON.stringify(buildLiveSessionStart({ mode, voice, instructions })),
      );
    };
    socket.onmessage = (message) => {
      if (wsRef.current !== socket) return;
      if (typeof message.data !== 'string') return;
      try {
        handleEvent(JSON.parse(message.data));
      } catch {}
    };
    socket.onerror = () => {
      if (wsRef.current !== socket) return;
      setErrorMessage((current) => current || 'WebSocket error');
    };
    socket.onclose = (event) => {
      if (wsRef.current !== socket) return;
      teardown(false);
      wsRef.current = null;
      setPhase('idle');
      if (statusRef.current !== 'error') setStatusBoth('closed');
      if (event.code !== 1000 && event.code !== 1005) {
        setErrorMessage(
          (current) =>
            current ||
            `连接关闭 · ${event.code}${event.reason ? ` · ${event.reason}` : ''}`,
        );
      }
    };
  };

  const disconnect = () => {
    connectionAttemptRef.current += 1;
    teardown(true);
    wsRef.current = null;
    setStatusBoth('closed');
    setPhase('idle');
  };

  const sendText = () => {
    const value = input.trim();
    if (!ready || !value) return;
    const startMs = sessionStartedAtRef.current
      ? Math.max(0, Math.round(performance.now() - sessionStartedAtRef.current))
      : null;
    transcriptRef.current = appendTranscriptFragment(
      transcriptRef.current,
      'user',
      { delta: value, start_ms: startMs, end_ms: startMs },
      true,
    );
    appendTurn('user', value, false, true);
    if (modeRef.current === 'client') {
      runTypedClientInput();
    } else {
      for (const event of buildTypedLiveEvents(modeRef.current, value)) {
        send(event);
      }
    }
    setInput('');
    setPhase('thinking');
  };

  const startMicrophone = async () => {
    if (
      statusRef.current !== 'ready' ||
      micStreamRef.current ||
      micRequestRef.current
    )
      return;
    setPhase('microphone');
    if (!isSecureAudioContext()) {
      setErrorMessage('麦克风需要 HTTPS 或 localhost。');
      setPhase('ready');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMessage('当前浏览器不支持麦克风采集。');
      setPhase('ready');
      return;
    }

    const socket = wsRef.current;
    const request = {};
    micRequestRef.current = request;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      if (
        micRequestRef.current !== request ||
        wsRef.current !== socket ||
        socket?.readyState !== WebSocket.OPEN ||
        statusRef.current !== 'ready'
      ) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) throw new Error('浏览器不支持 AudioContext');
      const context = new AudioContextClass();
      micContextRef.current = context;
      if (context.state === 'suspended') await context.resume();
      if (
        micRequestRef.current !== request ||
        wsRef.current !== socket ||
        socket?.readyState !== WebSocket.OPEN ||
        statusRef.current !== 'ready'
      ) {
        try {
          context.close();
        } catch {}
        if (micContextRef.current === context) micContextRef.current = null;
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      micStreamRef.current = stream;
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      micAnalyserRef.current = analyser;
      const processor = context.createScriptProcessor(4096, 1, 1);
      micNodeRef.current = processor;
      processor.onaudioprocess = (event) => {
        const activeSocket = wsRef.current;
        if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return;
        const pcm = floatToPcm16(
          event.inputBuffer.getChannelData(0),
          context.sampleRate,
        );
        if (
          pcm.length &&
          !send({
            type: 'session.input_audio.append',
            audio: bytesToBase64(pcm),
          })
        ) {
          stopMicrophone();
          setStatusBoth('ready');
          setPhase('ready');
        }
      };
      source.connect(processor);
      processor.connect(context.destination);
      setStatusBoth('recording');
      setPhase('listening');
    } catch (error) {
      if (stream && micStreamRef.current !== stream) {
        try {
          stream.getTracks().forEach((track) => track.stop());
        } catch {}
      }
      if (micRequestRef.current === request && wsRef.current === socket) {
        stopMicrophone();
        setStatusBoth('ready');
        setPhase('ready');
        setErrorMessage(`无法访问麦克风：${error?.message || error}`);
      }
    } finally {
      if (micRequestRef.current === request) micRequestRef.current = null;
    }
  };
  React.useEffect(() => {
    startMicrophoneRef.current = startMicrophone;
  });

  const latestUser = [...turns].reverse().find((turn) => turn.role === 'user');
  const latestAssistant = [...turns]
    .reverse()
    .find((turn) => turn.role === 'assistant');
  const statusColor =
    status === 'recording'
      ? 'var(--err)'
      : status === 'ready'
        ? 'var(--ok, #35c98a)'
        : status === 'connecting'
          ? 'var(--c-amber, #f0b23a)'
          : status === 'error'
            ? 'var(--err)'
            : 'var(--fg-4)';

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      <div className="card">
        <div
          className="row between"
          style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div className="micro">
            GPT Live 1 ·{' '}
            {selectableMode
              ? '单端点双模式'
              : fixedMode === 'responses'
                ? 'Official Responses'
                : 'Client'}
          </div>
          <div
            className="row gap-2"
            style={{ alignItems: 'center', fontSize: 12 }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: statusColor,
              }}
            />
            <span style={{ color: 'var(--fg-3)' }}>{status}</span>
          </div>
        </div>
        <div className="col gap-3" style={{ padding: 14 }}>
          <div
            className="row gap-3"
            style={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            {!connected ? (
              <button
                className="btn btn-primary"
                onClick={connect}
                disabled={status === 'connecting'}
                style={{ height: 40, minWidth: 130 }}
              >
                <XIcons.IconPlay size={14} /> 连接
              </button>
            ) : (
              <button
                className="btn"
                onClick={disconnect}
                style={{
                  height: 40,
                  minWidth: 130,
                  borderColor: 'var(--err)',
                  color: 'var(--err)',
                }}
              >
                <XIcons.IconPause size={14} /> 断开
              </button>
            )}
            <label
              className="row gap-2"
              style={{
                alignItems: 'center',
                fontSize: 12.5,
                color: 'var(--fg-3)',
              }}
            >
              <span>委派模式</span>
              <select
                className="input"
                value={mode}
                disabled={connected || !selectableMode}
                onChange={(event) => setMode(event.target.value)}
                style={{ height: 34 }}
              >
                {(selectableMode || fixedMode === 'client') && (
                  <option value="client">Client · xAPI AI Gateway</option>
                )}
                {(selectableMode || fixedMode === 'responses') && (
                  <option value="responses">Official · OpenAI Responses</option>
                )}
              </select>
            </label>
          </div>
          <div
            className="row gap-3"
            style={{ color: 'var(--fg-3)', fontSize: 12, flexWrap: 'wrap' }}
          >
            <span>
              Live{' '}
              <span className="mono" style={{ color: 'var(--fg-1)' }}>
                {liveSeconds.toFixed(1)}s
              </span>
              {livePerMinuteUsd > 0 && (
                <span className="mono">
                  {' '}
                  · 估算 ${liveEstimatedCost.toFixed(6)}
                </span>
              )}
            </span>
            <span>
              Delegation input{' '}
              <span className="mono" style={{ color: 'var(--fg-1)' }}>
                {delegationUsage.input}
              </span>
            </span>
            <span>
              output{' '}
              <span className="mono" style={{ color: 'var(--fg-1)' }}>
                {delegationUsage.output}
              </span>
            </span>
            {mode === 'responses' ? (
              <>
                <span>
                  cache write <span className="mono">{delegationUsage.cacheWrite}</span>
                </span>
                <span>
                  cached <span className="mono">{delegationUsage.cached}</span>
                </span>
                <span className="mono">
                  Official 估算 ${delegationUsage.estimatedCost.toFixed(6)}
                </span>
              </>
            ) : (
              <span>AI Gateway 模型调用单独计费</span>
            )}
          </div>
          {livePerMinuteUsd > 0 && (
            <div style={{ color: 'var(--fg-4)', fontSize: 11.5 }}>
              Live ${livePerMinuteUsd.toFixed(4)}/分钟 · 连接时临时预授权约 $
              {authorization.initialAuthorizationUsd.toFixed(4)}
              {authorization.maxSessionAuthorizationUsd != null
                ? ` · 会话授权上限 $${authorization.maxSessionAuthorizationUsd.toFixed(4)}`
                : ''}
              ；未使用部分在结算时释放。
              {mode === 'responses' && (
                <>
                  {' '}Official 模式在会话开始前
                  {authorization.officialStartAuthorizationUsd != null
                    ? `会临时预授权约 $${authorization.officialStartAuthorizationUsd.toFixed(4)}`
                    : '还需要额外临时预授权（当前后端未返回精确金额）'}
                  。
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div
          className="micro"
          style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          会话配置
        </div>
        <div className="col gap-3" style={{ padding: 14 }}>
          {mode === 'client' && (
            <div className="col gap-2">
              <label className="col gap-1">
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--fg-2)',
                    fontWeight: 600,
                  }}
                >
                  Delegation 模型
                </span>
                <SearchableSelect
                  value={modelId}
                  onChange={setModelId}
                  options={modelOptions}
                  label="Delegation 模型"
                  disabled={connected || modelsLoading}
                  placeholder={
                    modelsLoading
                      ? '正在加载 AI Gateway 模型…'
                      : models.length
                        ? '搜索模型或厂商…'
                        : '没有可用 Responses 文本模型'
                  }
                  emptyLabel="没有匹配的模型"
                  getSearchText={(model) =>
                    `${model.id} ${model.vendor || ''}`
                  }
                  renderOption={(model) => (
                    <>
                      <span className="searchable-select-model-id mono">
                        {model.id}
                      </span>
                      <span className="searchable-select-model-meta">
                        {model.vendor}
                        {model.providerCount
                          ? ` · ${model.providerCount} provider${
                              model.providerCount === 1 ? '' : 's'
                            }`
                          : ''}
                      </span>
                    </>
                  )}
                />
              </label>
              <label className="col gap-1">
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--fg-2)',
                    fontWeight: 600,
                  }}
                >
                  路由策略
                </span>
                <select
                  className="input"
                  value={strategy}
                  disabled={connected}
                  onChange={(event) => setStrategy(event.target.value)}
                  style={{ maxWidth: 240 }}
                >
                  {AI_GATEWAY_STRATEGIES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
              <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                浏览器只把 canonical model 和 XAPI_KEY 发给统一 AI
                Gateway；Provider 选择、协议适配与故障转移均由服务端完成。
              </span>
              {modelsError && (
                <span style={{ fontSize: 11, color: 'var(--err)' }}>
                  {modelsError}
                </span>
              )}
            </div>
          )}
          <label className="col gap-1">
            <span
              style={{ fontSize: 12, color: 'var(--fg-2)', fontWeight: 600 }}
            >
              对话个性化
            </span>
            <textarea
              className="input"
              rows={3}
              value={instructions}
              disabled={connected}
              maxLength={16_384}
              onChange={(event) => setInstructions(event.target.value)}
              placeholder="可选：设定语气和角色；固定的纯文本模型委派策略始终生效。"
            />
            <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
              {mode === 'client'
                ? '实质性请求会先委派给所选 AI Gateway 文本模型；当前不启用工具。'
                : `实质性请求由 OpenAI managed ${officialResponsesModel} 处理；当前不启用工具。`}
            </span>
          </label>
          <label className="col gap-1">
            <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>音色</span>
            <select
              className="input"
              value={voice}
              disabled={connected}
              onChange={(event) => setVoice(event.target.value)}
              style={{ maxWidth: 220 }}
            >
              {LIVE_BUILTIN_VOICES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {errorMessage && (
        <div
          className="row gap-2"
          style={{
            padding: '10px 12px',
            background: 'rgba(231,76,60,0.08)',
            border: '1px solid rgba(231,76,60,0.25)',
            borderRadius: 8,
            fontSize: 12.5,
            color: 'var(--err)',
          }}
        >
          <XIcons.IconInfo size={13} />{' '}
          <span style={{ overflowWrap: 'anywhere' }}>{errorMessage}</span>
        </div>
      )}

      <div className="card">
        <div
          className="row between"
          style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div className="micro">Live 对话</div>
          {turns.length > 0 && (
            <XPrim.CopyBtn
              text={turns
                .map((turn) => `${turn.role}: ${turn.text}`)
                .join('\n')}
            />
          )}
        </div>
        <div
          className="col"
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: '30px 16px',
            minHeight: 330,
            background:
              'radial-gradient(120% 80% at 50% 32%, rgba(80,120,220,0.06), transparent)',
          }}
        >
          <WsRealtimeOrb
            micAnalyserRef={micAnalyserRef}
            outAnalyserRef={outputAnalyserRef}
            phase={connected ? phase : 'idle'}
            size={200}
          />
          <div
            className="micro"
            style={{ color: 'var(--fg-3)', letterSpacing: 0.3 }}
          >
            {connected && phase === 'delegating'
              ? `正在委派给 ${
                  modeRef.current === 'client'
                    ? modelId || 'AI Gateway'
                    : officialResponsesModel
                }…`
              : PHASE_LABEL[connected ? phase : 'idle'] || phase}
          </div>
          <div
            className="col gap-1"
            style={{
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: 560,
              minHeight: 44,
            }}
          >
            {latestUser && (
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--fg-4)',
                  overflowWrap: 'anywhere',
                }}
              >
                {latestUser.text}
              </div>
            )}
            {latestAssistant && (
              <div
                style={{
                  fontSize: 15,
                  color: 'var(--fg-1)',
                  lineHeight: 1.5,
                  overflowWrap: 'anywhere',
                }}
              >
                {latestAssistant.text}
              </div>
            )}
            {!latestUser && !latestAssistant && (
              <div style={{ fontSize: 13, color: 'var(--fg-4)' }}>
                {status === 'recording'
                  ? '麦克风已自动开启，可直接说话或在下方发送文字。'
                  : ready
                    ? '麦克风未开启，仍可在下方发送文字。'
                    : '连接后开始 GPT Live 对话。'}
              </div>
            )}
          </div>
        </div>
        <div
          className="row gap-2"
          style={{
            padding: 12,
            borderTop: '1px solid var(--line)',
            alignItems: 'flex-end',
          }}
        >
          <textarea
            className="input mono"
            rows={2}
            style={{ resize: 'none', flex: 1 }}
            value={input}
            maxLength={MAX_TRANSCRIPT_CHARS}
            disabled={!ready}
            placeholder={ready ? '输入消息…（Enter 发送）' : '先连接…'}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                sendText();
              }
            }}
          />
          <button
            className="btn btn-primary"
            disabled={!ready || !input.trim()}
            onClick={sendText}
          >
            发送
          </button>
        </div>
      </div>

      <div className="card">
        <div
          className="micro"
          style={{
            padding: '10px 14px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          Delegation 状态
        </div>
        <div className="col gap-2" style={{ padding: 14 }}>
          {!delegations.length ? (
            <div style={{ color: 'var(--fg-4)', fontSize: 12 }}>
              尚未触发委派。
            </div>
          ) : (
            delegations.map((delegation) => (
              <div
                key={delegation.id}
                style={{
                  padding: '9px 10px',
                  border: '1px solid var(--line)',
                  borderRadius: 7,
                }}
              >
                <div className="row between gap-2">
                  <span
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-3)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {delegation.id}
                  </span>
                  <span className="tag" style={{ fontSize: 10 }}>
                    {delegation.mode} · {delegation.status}
                  </span>
                </div>
                {delegation.model && (
                  <div
                    style={{ marginTop: 5, fontSize: 11, color: 'var(--fg-4)' }}
                  >
                    {delegation.model} · {delegation.strategy || 'default'}{' '}
                    routing
                  </div>
                )}
                {delegation.content && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12.5,
                      whiteSpace: 'pre-wrap',
                      color: 'var(--fg-2)',
                    }}
                  >
                    {delegation.content}
                  </div>
                )}
                {delegation.error && (
                  <div
                    style={{ marginTop: 6, fontSize: 12, color: 'var(--err)' }}
                  >
                    {delegation.error}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
